// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/StatisticsSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2 } from 'react-icons/fi';

const StatisticsSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const statistics = websiteData?.statistics || { items: [] };

  // Local state parameters to fully insulate text focus inputs from parent updates
  const [localTitle, setLocalTitle] = useState(statistics.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(statistics.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(statistics.displayStyle || 'grid-4');
  const [localAnimate, setLocalAnimate] = useState(statistics.animateCounters !== false);
  const [localItems, setLocalItems] = useState(statistics.items || []);

  const [newItem, setNewItem] = useState({ label: '', value: '', prefix: '', suffix: '', icon: 'chart-bar' });
  const [saving, setSaving] = useState(false);

  // Sync isolated state only when component initial mounts or tenant context switches
  useEffect(() => {
    console.log('[StatisticsSection] Initializing isolated input values from baseline source data.');
    setLocalTitle(statistics.sectionTitle || '');
    setLocalSubtitle(statistics.sectionSubtitle || '');
    setLocalDisplayStyle(statistics.displayStyle || 'grid-4');
    setLocalAnimate(statistics.animateCounters !== false);
    setLocalItems(statistics.items || []);
  }, [statistics.items, statistics.sectionTitle, statistics.sectionSubtitle, statistics.displayStyle, statistics.animateCounters, tenantId]); // Added exact dependencies to clear the eslint error cleanly

  const handleToggle = () => {
    handleInputChange('statistics', 'isActive', !statistics.isActive);
  };

  const handleAddItem = () => {
    if (!newItem.label || !newItem.value) return;
    
    const item = {
      _id: Date.now().toString(),
      ...newItem,
      value: Number(newItem.value)
    };
    
    const updatedItems = [...localItems, item];
    console.log('[StatisticsSection] Structural row addition executed locally:', item);
    setLocalItems(updatedItems);
    
    // Sync structural layout arrays instantly to prevent baseline mapping sync discrepancies
    handleInputChange('statistics', 'items', updatedItems);

    setNewItem({ label: '', value: '', prefix: '', suffix: '', icon: 'chart-bar' });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[StatisticsSection] Structural row extraction at index position:', index);
    setLocalItems(updatedItems);
    
    // Sync structural layout arrays instantly to prevent baseline mapping sync discrepancies
    handleInputChange('statistics', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { 
      ...updatedItems[index], 
      [field]: field === 'value' ? (value === '' ? '' : Number(value)) : value 
    };
    
    console.log(`[StatisticsSection] Row input field altered locally [${index}] -> Key [${field}] -> Data [${value}]`);
    setLocalItems(updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[StatisticsSection] Pushing final section data layout state matrix up into global data tree context structure.');
    
    try {
      // Direct update of parent fields values synchronously before network submission call runs
      await handleInputChange('statistics', 'sectionTitle', localTitle);
      await handleInputChange('statistics', 'sectionSubtitle', localSubtitle);
      await handleInputChange('statistics', 'displayStyle', localDisplayStyle);
      await handleInputChange('statistics', 'animateCounters', localAnimate);
      await handleInputChange('statistics', 'items', localItems);

      const combinedPayload = {
        ...statistics,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        animateCounters: localAnimate,
        items: localItems
      };

      await handleSave('statistics', combinedPayload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Card Info Section */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Statistics Section</h2>
            <p className="text-sm text-slate-500 mt-0.5">Display key institutional metrics and performance metrics</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              onClick={handleToggle}
              type="button"
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                statistics.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {statistics.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              onClick={handleSaveClick}
              disabled={saving}
              type="button"
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Core Settings Input Layout block */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Layout Configurations</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Header Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleLocalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Our Impact by Numbers"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle Descriptive Row</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleLocalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Transforming lives through education"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-100 items-center">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleLocalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="grid-4">4 Columns Adaptive Grid</option>
                <option value="grid-3">3 Columns Adaptive Grid</option>
                <option value="carousel">Carousel View Slider</option>
              </select>
            </div>

            <div className="pt-4 md:pt-0 pl-1">
              <label className="flex items-center space-x-3 cursor-pointer group select-none">
                <input
                  type="checkbox"
                  checked={localAnimate}
                  onChange={(e) => handleLocalFieldChange('animateCounters', e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                  localAnimate 
                    ? 'bg-blue-600 border-blue-600 text-white' 
                    : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                }`}>
                  <span className={`w-2 h-2 rounded-sm bg-white ${localAnimate ? 'block' : 'hidden'}`} />
                </div>
                <span className="text-xs font-semibold text-slate-600 group-hover:text-slate-900 transition-colors">
                  Animate incremental counters on page view scroll position triggers
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Added Performance Score Blocks Map Container */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Added Performance Items</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Total Elements
            </span>
          </div>

          <div className="space-y-3">
            {localItems.map((item, index) => (
              <div key={item._id || index} className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wide">Label Field text</span>
                    <input
                      type="text"
                      value={item.label || ''}
                      onChange={(e) => handleItemChange(index, 'label', e.target.value)}
                      placeholder="e.g., Placed Students"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/40 focus:bg-white focus:border-blue-500"
                    />
                  </div>
                  
                  <div className="space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wide">Metric Numeric Value</span>
                    <input
                      type="number"
                      value={item.value ?? ''}
                      onChange={(e) => handleItemChange(index, 'value', e.target.value)}
                      placeholder="e.g., 2500"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/40 focus:bg-white focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wide">Prefix</span>
                    <input
                      type="text"
                      value={item.prefix || ''}
                      onChange={(e) => handleItemChange(index, 'prefix', e.target.value)}
                      placeholder="e.g., $"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/40 focus:bg-white focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-1">
                    <span className="text-[10px] font-bold uppercase text-slate-400 tracking-wide">Suffix String</span>
                    <input
                      type="text"
                      value={item.suffix || ''}
                      onChange={(e) => handleItemChange(index, 'suffix', e.target.value)}
                      placeholder="e.g., +"
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/40 focus:bg-white focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end md:pt-4 border-t border-slate-100 md:border-none pt-2">
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className="p-2 text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Add Fresh Metrics Object form block */}
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <div className="p-1 bg-blue-50 rounded-md text-blue-600">
              <FiPlus className="h-4 w-4 stroke-[3]" />
            </div>
            <h4 className="text-sm font-bold text-slate-800">Create New Metric Entry</h4>
          </div>

          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="w-full md:flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <input
                type="text"
                value={newItem.label}
                onChange={(e) => setNewItem({ ...newItem, label: e.target.value })}
                placeholder="Label Display text *"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="number"
                value={newItem.value}
                onChange={(e) => setNewItem({ ...newItem, value: e.target.value })}
                placeholder="Value Amount *"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.prefix}
                onChange={(e) => setNewItem({ ...newItem, prefix: e.target.value })}
                placeholder="Prefix Symbol"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
              <input
                type="text"
                value={newItem.suffix}
                onChange={(e) => setNewItem({ ...newItem, suffix: e.target.value })}
                placeholder="Suffix Symbol"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
              />
            </div>

            <button
              type="button"
              onClick={handleAddItem}
              className="w-full md:w-auto inline-flex items-center justify-center px-5 py-2 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
            >
              Insert Entry
            </button>
          </div>
        </div>

      </div>
    </div>
  );

  // Modular routing handler for unified input operations tracking internally
  function handleLocalFieldChange(field, value) {
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
    if (field === 'animateCounters') setLocalAnimate(value);
  }
};

export default StatisticsSection;